import { useState } from "react";
import { Card, CardContent, Paper, Typography, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { Draggable } from "react-beautiful-dnd";
import chukkerbookTheme from "../../chukkerbbokTheme";
import Grid from "@mui/material/Grid2";
import ProRequest from "../Signups/ProRequest";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

interface ProColorMapping {
  PROS: Record<number, [string, string]>;
  PLAYERS: Record<number, number>;
  MAPPING: Record<number, string>;
}

interface QuickAssignTarget {
  chukker: string;
  team: string;
  label: string;
}

interface PlayerItemEnhancedProps {
  id: string;
  managerMode: number;
  index: number;
  firstName: string;
  lastName: string;
  rating: number;
  chukker: string;
  team: string;
  isBench: boolean;
  proColorMapping?: ProColorMapping;
  betaMode: boolean;
  onQuickAssign?: (playerId: string, sourceChukker: string, sourceTeam: string, targetChukker: string, targetTeam: string) => void;
  availableTargets?: QuickAssignTarget[];
}

function PlayerItemEnhanced(props: PlayerItemEnhancedProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  let teamColor: { back: string; text: string } = {
    back: chukkerbookTheme["success"],
    text: "black",
  };
  
  if (props.team.match("A_")) {
    if (props.isBench) {
      teamColor = { back: chukkerbookTheme["secondary"], text: "black" };
    } else {
      teamColor = { back: chukkerbookTheme["warning"], text: "black" };
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleQuickAssign = (targetChukker: string, targetTeam: string) => {
    if (props.onQuickAssign) {
      props.onQuickAssign(props.id, props.chukker, props.team, targetChukker, targetTeam);
    }
    handleMenuClose();
  };

  const backgroundColor = props.proColorMapping &&
    props.proColorMapping["PROS"] &&
    props.proColorMapping["PROS"][props.index]
      ? props.proColorMapping["PROS"][props.index][0]
      : undefined;

  return (
    <Draggable
      draggableId={props.id}
      isDragDisabled={props.managerMode !== -1}
      index={parseInt(props.id)}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={"player-" + props.id}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.7 : 1,
            transform: snapshot.isDragging
              ? `${provided.draggableProps.style?.transform} rotate(2deg)`
              : provided.draggableProps.style?.transform,
            transition: snapshot.isDragging ? "none" : "all 0.2s ease",
          }}
        >
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
          >
            <Paper
              elevation={snapshot.isDragging ? 8 : 2}
              sx={{
                width: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
                cursor: props.managerMode === -1 ? "grab" : "default",
                "&:active": {
                  cursor: props.managerMode === -1 ? "grabbing" : "default",
                },
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  mx: -3,
                  textTransform: "none",
                  borderRadius: "12px",
                  color: "grey",
                  backgroundColor: backgroundColor || teamColor["back"],
                  border: snapshot.isDragging ? "2px solid" : "1px solid",
                  borderColor: snapshot.isDragging
                    ? chukkerbookTheme["primary"]
                    : "divider",
                }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid size="auto" sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        color={teamColor["text"]}
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.9rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {props.firstName + " " + props.lastName}
                      </Typography>
                      {(props.managerMode !== -1 || props.betaMode) && (
                        <Typography
                          color={teamColor["text"]}
                          sx={{
                            fontSize: "0.75rem",
                            opacity: 0.8,
                          }}
                        >
                          Rating: {props.rating}
                        </Typography>
                      )}
                    </Grid>
                    {props.managerMode === -1 && props.onQuickAssign && (
                      <Grid size="auto">
                        <Tooltip title="Quick assign">
                          <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            sx={{ ml: 0.5 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleMenuClose}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {props.availableTargets?.map((target) => (
                            <MenuItem
                              key={`${target.chukker}-${target.team}`}
                              onClick={() =>
                                handleQuickAssign(target.chukker, target.team)
                              }
                            >
                              <SwapHorizIcon sx={{ mr: 1, fontSize: 18 }} />
                              {target.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Grid>
                    )}
                  </Grid>
                  {props.proColorMapping &&
                    props.proColorMapping["PLAYERS"] &&
                    props.proColorMapping["PLAYERS"][props.index] && (
                      <ProRequest
                        requestedPro={
                          props.proColorMapping["PLAYERS"][props.index]
                        }
                        proColorMapping={props.proColorMapping["PROS"]}
                        proMapping={props.proColorMapping["MAPPING"]}
                      />
                    )}
                </CardContent>
              </Card>
            </Paper>
          </Grid>
        </div>
      )}
    </Draggable>
  );
}

export default PlayerItemEnhanced;

